import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useEffect, useMemo, useRef, useState } from "react";

const isShiftKey = (key: string): boolean =>
  key === "Shift" || key === "ShiftLeft" || key === "ShiftRight";

interface UseShiftMaterialHotkeysArgs {
  filteredMaterials: readonly { name: string }[];
  ownsKeyboard: () => boolean;
  canSetMaterial: () => boolean;
  onSetMaterial: (material: string | null) => void;
}

export const useShiftMaterialHotkeys = ({
  filteredMaterials,
  ownsKeyboard,
  canSetMaterial,
  onSetMaterial,
}: UseShiftMaterialHotkeysArgs): { isShiftHeld: boolean } => {
  const { getMaterialHotkey } = useHotkeyContext();
  const [isShiftHeld, setIsShiftHeld] = useState(false);

  const hotkeyCodeToMaterialIndex = useMemo(() => {
    const result: Record<string, number> = {};
    for (let index = -1; index < filteredMaterials.length; index++) {
      result[getMaterialHotkey(index)] = index;
    }
    return result;
  }, [filteredMaterials.length, getMaterialHotkey]);

  const ownsKeyboardRef = useRef(ownsKeyboard);
  ownsKeyboardRef.current = ownsKeyboard;
  const canSetMaterialRef = useRef(canSetMaterial);
  canSetMaterialRef.current = canSetMaterial;
  const onSetMaterialRef = useRef(onSetMaterial);
  onSetMaterialRef.current = onSetMaterial;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!ownsKeyboardRef.current()) return;

      if (isShiftKey(event.key) || isShiftKey(event.code) || event.shiftKey) {
        setIsShiftHeld(true);
      }

      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      if (!canSetMaterialRef.current()) return;
      if (!event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      if (event.repeat) return;

      const materialIndex = hotkeyCodeToMaterialIndex[event.code];
      if (materialIndex === undefined) return;
      if (materialIndex === -1) {
        event.preventDefault();
        onSetMaterialRef.current(null);
        return;
      }
      const material = filteredMaterials[materialIndex];
      if (material) {
        event.preventDefault();
        onSetMaterialRef.current(material.name);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (isShiftKey(event.key) || isShiftKey(event.code)) {
        setIsShiftHeld(false);
      }
    };

    const handleBlur = () => {
      setIsShiftHeld(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [hotkeyCodeToMaterialIndex, filteredMaterials]);

  return { isShiftHeld };
};
